import { withSocket } from './socketId.js'
import { notifySessionLost, coreFetch } from './http.js'
import { createOfflineClient, mergeBody } from '@addictivegroup/addictive-offline'
import { ENTITY_NAMES } from './entities.js'

/**
 * The one offline client of the panel (addictive-offline, D-35/D-37). Every record write
 * goes through `offline.save`: online first, queued when Core cannot be reached, delivered
 * in order later. Reads fall back to the replica the same way.
 */

/** Labels of records people picked in forms, so an optimistic write can show "Acme" rather than a uuid. */
const refLabels = new Map()

export function rememberLabel(ref) {
  if (ref?.uuid && ref.label) refLabels.set(ref.uuid, ref)
}

let currentUser = null
export function setCurrentUser(user) {
  currentUser = user
}

/** What a queued action does to the local copy until Core confirms it. */
// A closed activity is read-only until reopened (as the server's ActivityPolicy decides).
const ACTIVITY_CLOSED = { update: false, complete: false, cancel: false, reopen: true }
const ACTIVITY_OPEN = { update: true, complete: true, cancel: true, reopen: false }
const ACTION_EFFECTS = {
  complete: (row, body) => ({
    status: 'completed',
    completed_at: new Date().toISOString(),
    outcome: body.outcome ?? row.outcome ?? null,
    actual_duration_minutes: body.actual_duration_minutes ?? row.actual_duration_minutes ?? null,
    cancel_reason: null,
    abilities: { ...(row.abilities || {}), ...ACTIVITY_CLOSED },
  }),
  cancel: (row, body) => ({
    status: 'cancelled',
    completed_at: new Date().toISOString(),
    cancel_reason: body.reason ?? null,
    actual_duration_minutes: body.actual_duration_minutes ?? row.actual_duration_minutes ?? null,
    abilities: { ...(row.abilities || {}), ...ACTIVITY_CLOSED },
  }),
  reopen: (row, body, entity) => (entity === 'activity'
    ? { status: 'open', completed_at: null, outcome: null, cancel_reason: null, abilities: { ...(row.abilities || {}), ...ACTIVITY_OPEN } }
    : { status: 'open' }),
  close: (row, body) => ({ status: body.outcome === 'won' ? 'won' : 'lost', actual_close_date: body.actual_close_date || new Date().toISOString().slice(0, 10) }),
  qualify: () => ({ status: 'qualified', qualified_at: new Date().toISOString() }),
  disqualify: (row, body) => ({ status: 'disqualified', disqualify_reason: body.reason ?? null, disqualified_at: new Date().toISOString() }),
  reactivate: () => ({ status: 'open', qualified_at: null, disqualified_at: null }),
  'take-over': () => (currentUser ? { owner: { type: 'user', uuid: currentUser.uuid, label: currentUser.name } } : {}),
}

/** Optimistic local apply: references by uuid also update their {uuid,label} objects. */
export function applyLocal(entity, existing, body, operation) {
  const row = existing || { uuid: operation.uuid, entity, label: '', custom_fields: {}, tags: [], abilities: { view: true, update: true, delete: true } }
  const action = operation.path.split('/').pop()
  // An offer's document travels whole and lives beside the record, not in it.
  if (action === 'blocks') return { ...row, updated_at: operation.occurredAt }
  if (operation.method === 'POST' && ACTION_EFFECTS[action] && existing) {
    return { ...row, ...ACTION_EFFECTS[action](row, body || {}, entity) }
  }
  const merged = mergeBody(row, body || {})
  for (const [key, value] of Object.entries(body || {})) {
    if (key.endsWith('_uuid')) {
      const name = key.slice(0, -5)
      merged[name] = value ? refLabels.get(value) || { uuid: value, label: '…' } : null
    }
    if (key === 'owner' && value && typeof value === 'object') {
      merged.owner = { ...value, label: refLabels.get(value.uuid)?.label || value.label || '…' }
    }
  }
  if (!existing) {
    merged.label = merged.name || merged.topic || merged.subject || merged.title || [merged.first_name, merged.last_name].filter(Boolean).join(' ') || merged.label
    merged.number = merged.number || `TMP-${String(operation.uuid).slice(0, 8)}`
    merged.created_at = merged.created_at || operation.occurredAt
    if (!merged.owner && currentUser) merged.owner = { type: 'user', uuid: currentUser.uuid, label: currentUser.name }
  }
  merged.updated_at = operation.occurredAt
  return merged
}

export const offline = createOfflineClient({
  name: 'addcrm',
  apiBase: '/api/v1',
  entities: Object.fromEntries(ENTITY_NAMES.map((e) => [e, 'label'])),
  // A store per entity: a new entity is a new version of the device database (2: competitors,
  // 3: campaigns, marketing lists, events, 4: contracts, 5: agreements, 6: sales materials).
  dbVersion: 6,
  fetch: (url, init) => coreFetch(url, { ...(init || {}), headers: withSocket(init?.headers) }),
  currentUser: () => currentUser,
  applyLocal,
  // Refused for its session while sending the queue: signed in again (lib/reauth.js, D-246).
  onSessionLost: () => notifySessionLost(),
})
